<template>
  <section class="image-info">
    <div class="info-scroll-container">
      <!-- 1. 所属分类路径面包屑（带平滑骨架占位，彻底消除内容跳动） -->
      <div class="location-box" :class="{ 'is-loading': !previewData.path }">
        <v-icon small class="location-icon">mdi-folder-open-outline</v-icon>
        <a
          v-if="previewData.path"
          :href="`/${previewData.driveId}/${previewData.level1_fileId}`"
          class="location-link"
          :title="previewData.path"
        >
          {{ previewData.path }}
        </a>
        <span v-else class="location-placeholder">分类路径读取中...</span>
      </div>

      <!-- 2. 核心参数 2×2 精致磨砂磁贴网格（即时从已有数据提取，0ms秒开） -->
      <div class="meta-tiles-grid">
        <!-- 格式 -->
        <div class="meta-tile">
          <span class="meta-label">
            <v-icon x-small>mdi-file-outline</v-icon> 格式
          </span>
          <span class="meta-value format-badge">{{ previewData.ext || getExtFromName(previewData.name) || 'JPG' }}</span>
        </div>
        <!-- 尺寸 -->
        <div class="meta-tile">
          <span class="meta-label">
            <v-icon x-small>mdi-aspect-ratio</v-icon> 尺寸
          </span>
          <span class="meta-value">{{ previewData.width ? `${previewData.width} × ${previewData.height}` : '--' }}</span>
        </div>
        <!-- 大小 -->
        <div class="meta-tile">
          <span class="meta-label">
            <v-icon x-small>mdi-database-outline</v-icon> 大小
          </span>
          <span class="meta-value">{{ previewData.size || '--' }}</span>
        </div>
        <!-- 建立日期 -->
        <div class="meta-tile">
          <span class="meta-label">
            <v-icon x-small>mdi-clock-time-four-outline</v-icon> 建立日期
          </span>
          <span class="meta-value date-value" :title="previewData.createdTime || previewData.created_at">
            {{ formatShortDate(previewData.createdTime || previewData.created_at) }}
          </span>
        </div>
      </div>

      <!-- 3. 描述词板块（如有） -->
      <div class="description-card" v-if="previewData.description">
        <div class="desc-header">
          <span class="desc-title">
            <v-icon x-small>mdi-text-box-outline</v-icon> 描述词
          </span>
          <button class="desc-copy-btn" @click="copyDescription" title="复制描述词">
            <v-icon x-small :color="isCopied ? '#34d399' : '#94a3b8'">{{ isCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            <span :style="{ color: isCopied ? '#34d399' : '#94a3b8' }">{{ isCopied ? '已复制' : '复制' }}</span>
          </button>
        </div>
        <p class="desc-text">{{ previewData.description }}</p>
      </div>

      <!-- 4. 标签管理板块 -->
      <div class="tag-box-card">
        <div class="tag-header">
          <span class="tag-title">
            <v-icon x-small>mdi-tag-multiple-outline</v-icon> 标签 ({{ (previewData.tags && previewData.tags.length) || 0 }})
          </span>
        </div>
        <div class="tag-list-wrap">
          <v-btn
            class="tag"
            rounded
            v-for="(item, index) in previewData.tags"
            :key="item.id"
            :data-tag-name="item.name"
          >
            {{ item.name }}
            <div
              class="edit-operation"
              :data-tag-name="item.name"
              :data-tag-id="item.id"
              :data-tag-index="index"
            >
              <v-icon small>mdi-border-color</v-icon>
              <v-icon small>mdi-delete</v-icon>
            </div>
          </v-btn>
          <v-btn class="tag-plus" rounded outlined small>
            <v-icon left x-small>mdi-plus</v-icon>
            添加标签
          </v-btn>
        </div>
        <v-combobox
          ref="tagUpdate"
          v-show="isShowTagUpdate"
          v-model="chips"
          class="tag-update"
          :items="items"
          :search-input.sync="search"
          :loading="loading"
          @focus="on_focus"
          @keydown.enter="on_blur"
          @blur="on_blur"
          chips
          clearable
          multiple
          dark
          color="#10b981"
          single-line
          outlined
          dense
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>按回车创建新标签</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              v-bind="attrs"
              :input-value="selected"
              close
              color="#10b981"
              dark
              small
              @click="select"
              @click:close="remove(item)"
            >
              {{ item }}
            </v-chip>
          </template>
        </v-combobox>
      </div>

      <!-- 5. 互动操作区（点赞、踩、评论数） -->
      <div class="interaction-bar">
        <button
          class="inter-pill like-btn"
          :class="{ 'is-active': hasLike }"
          @click="on_toggle_likes"
          title="点赞"
        >
          <v-icon small :color="hasLike ? '#10b981' : '#94a3b8'">{{ hasLike ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}</v-icon>
          <span class="count">{{ previewData.likes || 0 }}</span>
        </button>

        <button
          class="inter-pill dislike-btn"
          :class="{ 'is-active': hasDislike }"
          @click="on_toggle_dislike"
          title="不喜欢"
        >
          <v-icon small :color="hasDislike ? '#f87171' : '#94a3b8'">{{ hasDislike ? 'mdi-thumb-down' : 'mdi-thumb-down-outline' }}</v-icon>
          <span class="count">{{ previewData.dislikes || 0 }}</span>
        </button>

        <div class="inter-pill comment-pill" title="评论数">
          <v-icon small color="#94a3b8">mdi-comment-outline</v-icon>
          <span class="count">{{ (previewData.comments && previewData.comments.length) || 0 }}</span>
        </div>
      </div>

      <!-- 6. 主操作下载区（翡翠品牌流光渐变大按钮） -->
      <div class="download-action-group">
        <button class="btn-emerald-download" @click="on_download(false)">
          <v-icon left small color="#fff">mdi-download</v-icon>
          下载素材
        </button>
        <button
          class="btn-psd-download"
          @click="on_download(true)"
          v-show="previewData.psdId"
          title="下载源 PSD 文件"
        >
          <v-icon left small color="#6ee7b7">mdi-file-download-outline</v-icon>
          下载 PSD
        </button>
      </div>

      <!-- 7. 评论模块 -->
      <comment
        :comments="previewData.comments || []"
        :fileId="previewData.fileId"
        @ccUpdateComment="on_update_card_info"
      />
    </div>
  </section>
</template>

<script>
import { get_api_data } from "@/utils/ajax";
import comment from "./compontents/comment/index";

export default {
  name: "image-info",
  components: {
    comment,
  },
  props: {
    previewInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      previewData: this.previewInfo,
      hasLike: false,
      hasDislike: false,
      isShowTagUpdate: false,
      onlyEdit: false,
      currentTag: {},
      loading: false,
      chips: [],
      items: [],
      search: null,
      select: null,
      operationTagId: null,
      isHideNoData: false,
      jpgSize: parseFloat(this.previewInfo.size || 0),
      isCopied: false,
    };
  },

  watch: {
    previewInfo(newVal) {
      this.previewData = newVal;
      this.hasLike = false;
      this.hasDislike = false;
    },

    search(val) {
      val && val !== this.select && this.query_selections(val);
    },
  },
  mounted() {
    this.init_edit_operation();
  },
  methods: {
    formatShortDate(dt) {
      if (!dt) return "--";
      return String(dt).split(" ")[0] || dt;
    },

    getExtFromName(name) {
      if (!name) return "";
      const parts = String(name).split(".");
      return parts.length > 1 ? parts.pop().toUpperCase() : "";
    },

    async query_selections(val) {
      this.loading = true;
      try {
        let tagReceive = await get_api_data("GET", `materials/tag/check`, {
          name: val,
        });
        if (tagReceive && tagReceive.data && tagReceive.data.length > 0) {
          tagReceive.data.map((el) => {
            this.items.push(el.name);
          });
        }
      } catch (e) {
        console.warn("查询标签异常:", e);
      } finally {
        this.loading = false;
      }
    },

    on_focus() {
      this.items.length = 0;
      this.chips.length = 0;
    },

    async on_blur(ev) {
      const chaip_task = (fileId, name) => {
        return () => {
          return creat_tag(fileId, name);
        };
      };

      const creat_tag = (fileId, name) => {
        this.chips = [];
        return get_api_data("POST", `materials/tag`, {
          fileIds: fileId,
          name,
        });
      };

      this.isHideNoData = true;

      if (this.onlyEdit) {
        if (this.chips.length === 0) return;
        let tagReceive = await get_api_data(
          "PATCH",
          `materials/tag/${this.operationTagId}`,
          {
            name: this.chips[0],
          }
        );
        if (tagReceive && tagReceive.status === 200) {
          this.chips.length = 0;
          this.$emit("ccUpdateInfo");
        }
      } else {
        let arrChipsTask = [];
        this.chips.map((name) => {
          arrChipsTask.push(chaip_task(this.previewData.fileId, name));
        });

        for (var i = 0; i < arrChipsTask.length; i++) {
          await arrChipsTask[i]();
        }

        this.$emit("ccUpdateInfo");
      }
    },

    remove(item) {
      this.chips.splice(this.chips.indexOf(item), 1);
      this.chips = [...this.chips];
    },

    init_edit_operation() {
      $(".tag-box-card").on("click", ".tag", (ev) => {
        this.$emit("ccClosePreview", ev.currentTarget.dataset.tagName);
      });

      $(".tag-box-card").on("click", ".edit-operation", async (ev) => {
        ev.stopPropagation();
        this.operationTagId = ev.currentTarget.dataset.tagId;
        this.currentTag = this.previewData.tags[
          ev.currentTarget.dataset.tagIndex
        ];

        if (ev.target.className.includes("delete")) {
          ev.stopPropagation();
          let tagReceive = await get_api_data(
            "PUT",
            `materials/tag/${this.operationTagId}`,
            {
              fileId: this.previewData.fileId,
            }
          );
          if (tagReceive && tagReceive.status === 200) {
            this.$emit("ccUpdateInfo");
          }
        } else if (ev.target.className.includes("border-color")) {
          this.isShowTagUpdate = true;
          this.onlyEdit = true;
          this.search = null;
          this.$nextTick(() => {
            this.chips.length = 0;
            this.search = ev.currentTarget.dataset.tagName;
            this.$refs.tagUpdate && this.$refs.tagUpdate.focus();
          });
        }
      });

      $(".tag-plus").on("click", (ev) => {
        this.isShowTagUpdate = true;
        this.onlyEdit = false;
        this.$nextTick(() => {
          this.$refs.tagUpdate && this.$refs.tagUpdate.focus();
        });
      });
    },

    async on_toggle_likes() {
      if (this.hasDislike) {
        this.on_toggle_dislike();
      }
      this.hasLike ? this.previewData.likes-- : this.previewData.likes++;
      await get_api_data("POST", "materials/like", {
        fileIds: [this.previewData.fileId],
        action: this.hasLike ? "dec" : "inc",
      });
      this.hasLike = !this.hasLike;
      this.$emit(
        "ccUpdateListCardOperation",
        "likes",
        this.previewData.likes
      );
    },

    async on_toggle_dislike() {
      if (this.hasLike) {
        this.on_toggle_likes();
      }
      this.hasDislike
        ? this.previewData.dislikes--
        : this.previewData.dislikes++;
      await get_api_data("POST", "materials/dislike", {
        fileIds: [this.previewData.fileId],
        action: this.hasDislike ? "dec" : "inc",
      });
      this.hasDislike = !this.hasDislike;
      this.$emit(
        "ccUpdateListCardOperation",
        "dislikes",
        this.previewData.dislikes
      );
    },

    async on_download(isHasPsd) {
      await get_api_data("POST", "materials/downloads", {
        fileIds: [this.previewData.fileId],
      });

      this.previewData.downloads = (this.previewData.downloads || 0) + 1;
      if (isHasPsd) {
        window.open(this.previewData.download_psd, "_blank");
      } else {
        let openType = this.jpgSize > 100 ? "_blank" : "_self";
        window.open(this.previewData.download_url, openType);
      }
      this.$emit(
        "ccUpdateListCardOperation",
        "download",
        this.previewData.downloads
      );
    },

    on_update_card_info() {
      this.$emit("ccUpdateInfo");
    },

    copyDescription() {
      if (!this.previewData.description) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(this.previewData.description);
        } else {
          let input = document.createElement("textarea");
          input.value = this.previewData.description;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      } catch (err) {
        console.error("复制失败:", err);
      }
    },
  },
};
</script>

<style lang="scss">
.image-info {
  position: relative;
  flex: 0 0 380px;
  width: 380px;
  max-width: 380px;
  height: 100%;
  background: rgba(13, 20, 36, 0.78);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0;
  box-shadow: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 10;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  .info-scroll-container {
    padding: 24px 22px;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 16px;

    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.15);
      border-radius: 10px;
    }
  }

  // 1. 位置路径胶囊
  .location-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);

    .location-icon {
      color: #34d399;
      flex-shrink: 0;
    }

    .location-placeholder {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.4);
      letter-spacing: 0.02em;
    }

    &.is-loading {
      opacity: 0.75;
    }

    .location-link {
      font-size: 13px;
      font-weight: 500;
      color: #94a3b8;
      text-decoration: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: color 0.18s ease;

      &:hover {
        color: #34d399;
        text-decoration: underline;
      }
    }
  }

  // 2. 2×2 精致磨砂参数磁贴
  .meta-tiles-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    .meta-tile {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 10px 12px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.07);
      border-radius: 12px;
      transition: background 0.2s ease, border-color 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.07);
        border-color: rgba(16, 185, 129, 0.3);
      }

      .meta-label {
        font-size: 11px;
        color: #64748b;
        display: flex;
        align-items: center;
        gap: 4px;
        text-transform: uppercase;
        letter-spacing: 0.04em;

        .v-icon {
          color: #64748b;
        }
      }

      .meta-value {
        font-size: 13px;
        font-weight: 600;
        color: #f1f5f9;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &.format-badge {
          display: inline-block;
          width: fit-content;
          padding: 2px 8px;
          border-radius: 6px;
          background: rgba(16, 185, 129, 0.18);
          color: #34d399;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        &.date-value {
          font-size: 12px;
          color: #cbd5e1;
        }
      }
    }
  }

  // 3. 描述词板块
  .description-card {
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 12px;

    .desc-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;

      .desc-title {
        font-size: 12px;
        font-weight: 600;
        color: #94a3b8;
        display: flex;
        align-items: center;
        gap: 4px;

        .v-icon {
          color: #94a3b8;
        }
      }

      .desc-copy-btn {
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        padding: 2px 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: #94a3b8;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(16, 185, 129, 0.15);
          border-color: rgba(16, 185, 129, 0.4);
          color: #34d399;
        }
      }
    }

    .desc-text {
      font-size: 13px;
      line-height: 1.6;
      color: #cbd5e1;
      margin: 0;
      word-break: break-word;
    }
  }

  // 4. 标签板块
  .tag-box-card {
    padding: 14px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 12px;

    .tag-header {
      margin-bottom: 10px;
      .tag-title {
        font-size: 12px;
        font-weight: 600;
        color: #94a3b8;
        display: flex;
        align-items: center;
        gap: 4px;

        .v-icon {
          color: #94a3b8;
        }
      }
    }

    .tag-list-wrap {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;

      .tag {
        position: relative;
        background: rgba(16, 185, 129, 0.12) !important;
        border: 1px solid rgba(16, 185, 129, 0.3) !important;
        color: #6ee7b7 !important;
        font-size: 12px !important;
        font-weight: 500;
        letter-spacing: 0.02em;
        text-transform: none;
        height: 28px !important;
        padding: 0 12px !important;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(16, 185, 129, 0.22) !important;
          border-color: #34d399 !important;
          color: #ffffff !important;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);

          .edit-operation {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }
        }

        .edit-operation {
          position: absolute;
          bottom: 34px;
          left: 50%;
          transform: translateX(-50%) translateY(4px);
          height: 32px;
          padding: 2px 8px;
          border-radius: 8px;
          background: rgba(15, 23, 42, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.18);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
          display: flex;
          align-items: center;
          gap: 6px;
          opacity: 0;
          pointer-events: none;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 100;

          .v-icon {
            color: #94a3b8;
            font-size: 15px;
            cursor: pointer;
            transition: color 0.15s ease;

            &:hover {
              color: #34d399;
            }

            &:last-of-type:hover {
              color: #f87171;
            }
          }
        }
      }

      .tag-plus {
        height: 28px !important;
        font-size: 11px !important;
        color: #94a3b8 !important;
        border-color: rgba(255, 255, 255, 0.15) !important;
        background: transparent !important;

        &:hover {
          border-color: #34d399 !important;
          color: #34d399 !important;
          background: rgba(16, 185, 129, 0.1) !important;
        }
      }
    }

    .tag-update {
      margin-top: 10px;
    }
  }

  // 5. 互动操作区
  .interaction-bar {
    display: flex;
    align-items: center;
    gap: 10px;

    .inter-pill {
      flex: 1;
      height: 38px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #94a3b8;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      outline: none;

      &:hover:not(.comment-pill) {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.2);
        color: #f1f5f9;
        transform: translateY(-1px);
      }

      &.is-active.like-btn {
        background: rgba(16, 185, 129, 0.16);
        border-color: rgba(16, 185, 129, 0.4);
        color: #34d399;
      }

      &.is-active.dislike-btn {
        background: rgba(239, 68, 68, 0.16);
        border-color: rgba(239, 68, 68, 0.4);
        color: #f87171;
      }

      &.comment-pill {
        cursor: default;
      }
    }
  }

  // 6. 主操作下载按键区（重铸核心）
  .download-action-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;

    .btn-emerald-download {
      width: 100%;
      height: 48px;
      border-radius: 12px;
      background: linear-gradient(135deg, #10b981 0%, #0d9488 100%);
      border: none;
      color: #ffffff;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0.03em;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 8px 24px -4px rgba(16, 185, 129, 0.5);
      transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
      outline: none;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px -4px rgba(16, 185, 129, 0.65);
        background: linear-gradient(135deg, #34d399 0%, #0f766e 100%);
      }

      &:active {
        transform: translateY(1px);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
      }
    }

    .btn-psd-download {
      width: 100%;
      height: 42px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(52, 211, 153, 0.4);
      color: #6ee7b7;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.02em;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      outline: none;

      &:hover {
        background: rgba(16, 185, 129, 0.15);
        border-color: #34d399;
        color: #ffffff;
        box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(1px);
      }
    }
  }
}
</style>
